import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { AttemptPending } from "@/components/portal/lms/AttemptPending";
import { AttemptResult } from "@/components/portal/lms/AttemptResult";
import { AttemptRunner } from "@/components/portal/lms/AttemptRunner";
import { PortalApiError } from "@/lib/portalApi";
import { lmsLearnerApi, type LearnerAttemptView } from "@/services/api/lms";

/** How far along its life an attempt is. An attempt only ever moves forward:
 *  in_progress → submitted/auto_graded/under_review → final. */
const STATE_RANK: Record<LearnerAttemptView["state"], number> = {
  in_progress: 0,
  submitted: 1,
  auto_graded: 1,
  under_review: 1,
  final: 2,
};

export const Route = createFileRoute("/portal/learning/attempt/$attemptId")({
  component: AttemptRoute,
});

/** Keyed by id: the router reuses this component when only the param changes,
 *  and every piece of per-attempt state (the attempt, the anchor, the
 *  once-per-outage toast flag) must start fresh for a different attempt. */
function AttemptRoute() {
  const { attemptId } = Route.useParams();
  return <AttemptPage key={attemptId} attemptId={attemptId} />;
}

/**
 * One URL for an attempt's whole life (Wave C D3): the page reads the attempt
 * and renders by `state` — the runner while `in_progress`, the pending view
 * once submitted, the released result at `final`. dp picks the DTO family by
 * state, so the result view only ever receives `LearnerFinalAttempt`.
 *
 * Owner-scoped server-side: any miss (another learner's attempt, unknown id)
 * is a 404, rendered as "not found" with no further detail.
 */
function AttemptPage({ attemptId }: { attemptId: string }) {
  const { token } = useAuth();
  const [attempt, setAttempt] = useState<LearnerAttemptView | null>(null);
  // Bumped on every fresh response, so the clock re-anchors even when a
  // re-read returns the same seconds_remaining.
  const [anchor, setAnchor] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadNotFound, setLoadNotFound] = useState(false);

  const loadedRef = useRef(false);
  // A re-read failing every 60 s while dp is down says so once, not every
  // minute; the next success re-arms it.
  const refreshFailingRef = useRef(false);
  // Responses can land out of order: a re-read sent before a Submit (e.g. the
  // recovery GET after a timed-out submit) may answer after it. The attempt
  // never moves backwards, so a response older in state than the one on
  // screen is stale and is dropped — it must not put a submitted attempt back
  // in the live runner.
  const shownRef = useRef<LearnerAttemptView | null>(null);
  const replace = useCallback((next: LearnerAttemptView) => {
    loadedRef.current = true;
    const shown = shownRef.current;
    if (shown && STATE_RANK[next.state] < STATE_RANK[shown.state]) return;
    shownRef.current = next;
    setAttempt(next);
    setAnchor((n) => n + 1);
  }, []);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      replace(await lmsLearnerApi.getAttempt(token, attemptId));
      setLoadError(null);
      refreshFailingRef.current = false;
    } catch (err) {
      const notFound = err instanceof PortalApiError && err.status === 404;
      const message = notFound ? "Attempt not found." : err instanceof Error ? err.message : "Failed to load";
      setLoadError(message);
      setLoadNotFound(notFound);
      // Once the attempt is on screen the not-found card no longer renders, so
      // say it here (once per outage); the runner's periodic re-read retries
      // within a minute.
      if (loadedRef.current && !refreshFailingRef.current) {
        refreshFailingRef.current = true;
        toast.error(`Couldn't refresh this test: ${message}`);
      }
    }
  }, [token, attemptId, replace]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loadError && !attempt) {
    return (
      <Card>
        <CardContent className="py-12 text-center" data-testid="lms-attempt-not-found">
          <p className="text-destructive">{loadError}</p>
          <div className="mt-4 flex justify-center gap-2">
            {/* A timeout or outage is worth another go; a 404 is not. */}
            {!loadNotFound && (
              <Button
                onClick={() => {
                  setLoadError(null);
                  void load();
                }}
                data-testid="lms-attempt-load-retry"
              >
                Try again
              </Button>
            )}
            <Button asChild variant="outline">
              <Link to="/portal/learning">Back to My tests</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!attempt || !token) {
    return (
      <div className="flex justify-center py-12 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`${attempt.test_title} | My tests`}</title>
      </Helmet>
      {attempt.state === "final" ? (
        <AttemptResult attempt={attempt} />
      ) : attempt.state === "in_progress" ? (
        <AttemptRunner token={token} attempt={attempt} anchor={anchor} reload={load} replace={replace} />
      ) : (
        <AttemptPending attempt={attempt} />
      )}
    </>
  );
}
